// Card — bordered container for grouping content.
//
// The original primitive (before the Citycare brand v1.0 rollout)
// rendered a single hard-coded surface: `rounded-lg border
// border-gray-200 bg-white p-4 shadow-sm`. That's preserved as the
// `tone="legacy"` default so existing consumers keep working without
// a sweep.
//
// New brand-correct rendering is opt-in via `tone="brand"`:
//   - rounded-2xl (brand 16px) instead of rounded-lg (8px)
//   - border-border (Stone Grey token) instead of border-gray-200
//   - optional `padding` ("compact" 16px / "primary" 24px)
//   - optional `surface` ("light" white / "dark" Forest @ 3%)
//   - optional `variant="interactive"` for hover lift
//     (translateY(-4px) + deeper shadow with motion-reduce: carve-out)
//
// Both tones forward arbitrary DOM attributes (`data-testid`, ARIA,
// etc.) and merge caller-provided className. The legacy contract is
// covered by card.test.tsx.

import type { ElementType, HTMLAttributes, ReactNode } from 'react'

export type CardTone = 'legacy' | 'brand'
export type CardPadding = 'compact' | 'primary'
export type CardSurface = 'light' | 'dark'
export type CardVariant = 'static' | 'interactive'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `legacy` (default) keeps the original `rounded-lg` shell so
   *  existing consumers don't change. `brand` switches to the
   *  Citycare brand v1.0 card spec (rounded-2xl, brand tokens,
   *  optional hover lift). */
  tone?: CardTone
  /** Internal padding when `tone="brand"`. Ignored on `legacy`. */
  padding?: CardPadding
  /** Background context when `tone="brand"`. Ignored on `legacy`. */
  surface?: CardSurface
  /** Hover lift when `tone="brand"`. Ignored on `legacy`. */
  variant?: CardVariant
  /** Render-as override (defaults to `<div>`). */
  as?: ElementType
  children: ReactNode
}

const LEGACY_CLASSES =
  'rounded-lg border border-gray-200 bg-white p-4 shadow-sm'

const BRAND_BASE =
  'rounded-2xl border border-border transition-[transform,box-shadow] duration-200 ease-out'

const BRAND_PADDING: Record<CardPadding, string> = {
  compact: 'p-4',
  primary: 'p-6',
}

const BRAND_SURFACE: Record<CardSurface, string> = {
  light: 'bg-white shadow-sm',
  // Forest at 3% for dark-context cards. Lighter shadow because
  // elevation reads differently against the deeper page colour.
  dark: 'bg-[color:var(--color-brand-deep)]/[0.03] shadow-none',
}

const BRAND_VARIANT: Record<CardVariant, string> = {
  static: '',
  // motion-reduce: carve-out keeps users with the OS-level
  // reduce-motion preference on a static card (vestibular safety).
  interactive:
    'hover:-translate-y-1 hover:shadow-md motion-reduce:hover:transform-none motion-reduce:hover:shadow-sm',
}

function brandClasses(
  padding: CardPadding,
  surface: CardSurface,
  variant: CardVariant,
): string {
  return [
    BRAND_BASE,
    BRAND_PADDING[padding],
    BRAND_SURFACE[surface],
    BRAND_VARIANT[variant],
  ].join(' ')
}

export function Card({
  tone = 'legacy',
  padding = 'compact',
  surface = 'light',
  variant = 'static',
  as: Component = 'div',
  className,
  children,
  ...rest
}: CardProps) {
  const baseClasses =
    tone === 'brand' ? brandClasses(padding, surface, variant) : LEGACY_CLASSES
  const classes = [baseClasses, className ?? '']
    .filter((c) => c !== '')
    .join(' ')
  return (
    <Component className={classes} {...rest}>
      {children}
    </Component>
  )
}
